import React, {useEffect, useState} from "react";
import {NetworkCalls} from "../../NetworkCalls";
import LoadingPageIndicator from "../../components/LoadingPageIndicator";
import uuid from "react-uuid";
import HandleHttpResponses from "../../components/NetworkErrorHandling";
import axios, {AxiosError} from "axios";
import {CatchReport} from "../../dtos/CatchReport";
import TimestampFormat from "../../utils/TimestampFormat";
import { Dropdown } from "react-bootstrap";
import DeleteDialog from "../../components/DeleteDialog";
import {DownloadExcelCsvButton} from "../../components/DownloadExcelCsvButton";
import {useNavigate} from "react-router-dom";
import {useSelectedOrganisationContext} from "../../custom-hooks/useOrganisation";

export default function CatchReports() {
    const navigate = useNavigate();
    const [loading, isLoading] = useState<boolean>(false);
    const [catchReport, setCatchReport] = useState<CatchReport[]>([]);
    const [error, setError] = useState<AxiosError>();
    const [selectedYear, setSelectedYear] = useState<number>(-1);
    const [years, setYears] = useState<number[]>([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
    const [selectedCatchReport, setSelectedCatchReport] = useState<
        CatchReport | undefined
    >(undefined);

    // Pagination
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(25);

    // Search
    const [searchQuery, setSearchQuery] = useState<string>("");

    const { selectedOrganisation } = useSelectedOrganisationContext();

    useEffect(() => {
        isLoading(true);
        NetworkCalls.getCatchReportYears()
            .then((result) => {
                const years = result.sort(sortByYear);
                setYears(years);
                setSelectedYear(years[0]);
            })
            .finally(() => isLoading(false));
    }, []);

    useEffect(() => {
        if (selectedYear > 2010) {
            isLoading(true);
            setCurrentPage(1); // Reset to first page when year changes
            NetworkCalls.getCatchReports(selectedYear)
                .then((result) => {
                    setCatchReport(result);
                })
                .catch((error) => {
                    setError(error);
                })
                .finally(() => isLoading(false));
        }
    }, [selectedYear]);

    // Filter by search query
    const filteredData = catchReport.filter((item) => {
        if (!searchQuery.trim()) return true;
        const memberId = item.memberId?.toLowerCase() || '';
        const guestCardTransaction = item.guestCardTransaction?.toLowerCase() || '';
        const query = searchQuery.toLowerCase();
        return memberId.includes(query) || guestCardTransaction.includes(query);
    });

    // Pagination calculations
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    function sortByYear(a: number, b: number) {
        return b - a;
    }

    const options = years.map((year) => {
        return (
            <option key={year} value={year}>
                {year}
            </option>
        );
    });

    function showContent() {
        if (!selectedOrganisation) {
            return (<>Zuerst Organisation auswählen</>);
        }
        return (
            <div className="container-fluid px-0" style={{ height: 'calc(100vh - 120px)', overflow: 'hidden' }}>
                <div className="row g-0 h-100">
                    {/* Filter Sidebar */}
                    <div className="col-12 col-md-3 col-lg-2 bg-light border-end h-100" style={{ overflowY: 'auto' }}>
                        <div className="p-3">
                            <h5 className="mb-3">Filter</h5>

                            <div className="mb-4">
                                <label htmlFor="yearControl" className="form-label fw-semibold">Jahr</label>
                                <select
                                    id="yearControl"
                                    className="form-select"
                                    onChange={(elem) => setSelectedYear(Number(elem.target.value))}
                                    defaultValue={selectedYear}
                                >
                                    {options}
                                </select>
                            </div>

                            <div className="mb-4">
                                <label className="form-label fw-semibold">Excel Download</label>
                                <DownloadExcelCsvButton
                                    downloadUrl={`${axios.defaults.baseURL}/catch/getCatchReportExcel?organisationId=${selectedOrganisation.id}&year=${selectedYear}`}
                                />
                            </div>

                            <div
                                className="mb-3"
                                hidden={
                                    !(
                                        selectedOrganisation.id === "d225ca9958a567ee0158abb195000023" ||
                                        selectedOrganisation.id === "2c92808394a2e1a50194cd49b3ec0001"
                                    )
                                }
                            >
                                <label className="form-label fw-semibold">iSL CSV Download</label>
                                <DownloadExcelCsvButton
                                    downloadUrl={`${axios.defaults.baseURL}/usercatch/${selectedOrganisation.id}/catch-report-list/isl-export-csv?year=${selectedYear}`}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="col-12 col-md-9 col-lg-10 h-100" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <div className="p-3 h-100 d-flex flex-column" style={{ minHeight: 0 }}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="m-0">Fangmeldungen {selectedYear}</h4>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="input-group input-group-sm" style={{ width: '220px' }}>
                                        <span className="input-group-text bg-white">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="11" cy="11" r="8"></circle>
                                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                            </svg>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Mitgliedsnummer..."
                                            value={searchQuery}
                                            onChange={(e) => {
                                                setSearchQuery(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        />
                                        {searchQuery && (
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => {
                                                    setSearchQuery("");
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: 'auto' }}
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            setItemsPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value={10}>10 pro Seite</option>
                                        <option value={25}>25 pro Seite</option>
                                        <option value={50}>50 pro Seite</option>
                                        <option value={100}>100 pro Seite</option>
                                    </select>
                                </div>
                            </div>

                            <div className="card shadow-sm flex-grow-1" style={{ minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                <div className="table-responsive flex-grow-1" style={{ overflowY: 'auto' }}>
                                    {showCatchReportTable()}
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="card-footer bg-white border-top d-flex justify-content-between align-items-center py-2">
                                        <span className="text-muted small">
                                            Zeige {startIndex + 1} - {Math.min(endIndex, filteredData.length)} von {filteredData.length}
                                        </span>
                                        <nav>
                                            <ul className="pagination pagination-sm mb-0">
                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(1)}
                                                        disabled={currentPage === 1}
                                                    >
                                                        «
                                                    </button>
                                                </li>
                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                        disabled={currentPage === 1}
                                                    >
                                                        ‹
                                                    </button>
                                                </li>
                                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                    let pageNum: number;
                                                    if (totalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= totalPages - 2) {
                                                        pageNum = totalPages - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }
                                                    return (
                                                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => setCurrentPage(pageNum)}
                                                            >
                                                                {pageNum}
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        ›
                                                    </button>
                                                </li>
                                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(totalPages)}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        »
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <DeleteDialog
                    onDeleteConfirm={async () => {
                        setShowDeleteDialog(false);
                        if (selectedCatchReport !== undefined) {
                            isLoading(true);
                            await NetworkCalls.deleteCatchReport(selectedCatchReport);
                            setCatchReport(await NetworkCalls.getCatchReports(selectedYear));
                            setSelectedCatchReport(undefined);
                            isLoading(false);
                        }
                    }}
                    title="Löschen"
                    message="Datensatz wirklich löschen?"
                    show={showDeleteDialog}
                    onHandleClose={() => setShowDeleteDialog(false)}
                />
            </div>
        );
    }

    function showCatchReportTable() {
        return (
            <table className="table table-hover table-striped mb-0">
                <thead className="table-light sticky-top">
                <tr key={uuid()}>
                    <th scope="col">Mitgliedsnummer</th>
                    <th scope="col">Gewässer</th>
                    <th scope="col">Fisch</th>
                    <th scope="col">Zeitpunkt</th>
                    <th scope="col">Länge (cm)</th>
                        <th scope="col">Gewicht (g)</th>
                    <th scope="col">Entnommen</th>
                        <th scope="col" style={{ width: '60px' }}></th>
                </tr>
                </thead>
                <tbody id="table-content">{createRows()}</tbody>
            </table>
        );
    }

    function getWasTakenIcon(value: Boolean | null | undefined) {
        if (value === true) {
            return (
                <span className="badge bg-success-lt text-success" title="Entnommen">
                    ✓
                </span>
            );
        }
        if (value === false) {
            return (
                <span className="badge bg-secondary-lt text-muted" title="Zurückgesetzt">
                    –
                </span>
            );
        }
        return (
            <span className="badge bg-warning-lt text-warning" title="Unbekannt">
                ?
            </span>
        );
    }

    function createRows() {
        return paginatedData.map((elem) => {
            return (
                <tr key={uuid()} className="user-select-auto">
                    <td>{elem.memberId ? elem.memberId : elem.guestCardTransaction}</td>
                    <td>{elem.waterName}</td>
                    <td>{elem.fishName}</td>
                    <td>{TimestampFormat.toDateTimeReadableFormat(elem.catchTime)}</td>
                    <td>{elem.fishLength}</td>
                    <td>{elem.fishWeight}</td>
                    <td>{getWasTakenIcon(elem.wasTaken)}</td>
                    <td>
                        <Dropdown align="end">
                            <Dropdown.Toggle
                                variant="link"
                                className="btn btn-link p-1 text-decoration-none text-primary fs-5 lh-1"
                                style={{ fontSize: '1.5rem' }}
                            >
                                ⋮
                            </Dropdown.Toggle>
                            <Dropdown.Menu style={{ backgroundColor: "#fff", zIndex: 1060 }}>
                                <Dropdown.Item
                                    onClick={() => navigate(`/dashboard/catch-report/edit/${elem.id}`)}
                                >
                                    Bearbeiten
                                </Dropdown.Item>
                                <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => {
                                        setShowDeleteDialog(true);
                                        setSelectedCatchReport(elem);
                                    }}
                                >
                                    Löschen
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </td>
                </tr>
            );
        });
    }

    return (
        <div>
            {loading ? (
                <LoadingPageIndicator/>
            ) : error ? (
                HandleHttpResponses(error)
            ) : (
                showContent()
            )}
        </div>
    );
}
